import { t } from "@lingui/macro";
import { Download, FileText } from "@phosphor-icons/react";
import React from "react";

import { useCssAnimation } from "../hooks/use-css-animation";
import { EnhancedProgressIndicator } from "./enhanced-progress-indicator";

type PdfLoadingModalProps = {
  isOpen: boolean;
  progress?: number;
  steps?: {
    id: string;
    title: string;
    description?: string;
    status: "pending" | "in-progress" | "completed" | "error";
    progress?: number;
  }[];
};

export const PdfLoadingModal: React.FC<PdfLoadingModalProps> = ({
  isOpen,
  progress = 0,
  steps,
}) => {
  const { shouldRender, isExiting } = useCssAnimation(isOpen, {
    duration: 200,
    exitDuration: 200,
  });

  if (!shouldRender) return null;

  const animationClass = isExiting ? "fade-out" : "fade-in";
  const scaleClass = isExiting ? "scale-out" : "scale-in";

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm ${animationClass}`}
        style={{ animationDuration: "200ms" }}
      />

      {/* Modal */}
      <div
        className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 ${animationClass} ${scaleClass}`}
        style={{ animationDuration: "200ms" }}
      >
        <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 dark:from-blue-500/5 dark:to-purple-500/5" />

          {/* Content */}
          <div className="relative p-8">
            {/* Icon Animation */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                {/* Rotating gradient blur - CSS animation */}
                <div className="animate-spin-slow absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 opacity-20 blur-xl" />
                <div className="relative rounded-full bg-gradient-to-br from-blue-500 to-purple-500 p-4">
                  <FileText size={32} className="text-white" />
                </div>
                {/* Download icon badge */}
                <div className="absolute -bottom-1 -right-1 animate-scale-in rounded-full bg-green-500 p-1">
                  <Download size={16} className="text-white" />
                </div>
              </div>
            </div>

            {/* Text */}
            <h3 className="mb-2 text-center text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t`Generating Your PDF`}
            </h3>
            <p className="mb-6 text-center text-sm text-gray-600 dark:text-gray-400">
              {t`Please wait while we create your professional resume PDF...`}
            </p>

            {/* Progress Indicator */}
            {steps ? (
              <EnhancedProgressIndicator steps={steps} className="space-y-3" />
            ) : (
              /* Fallback Progress Bar */
              <div className="relative h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                <div className="animate-progress absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-purple-500" />
                <div className="animate-shimmer absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              </div>
            )}

            {/* Loading Dots - CSS animation */}
            <div className="mt-6 flex items-center justify-center gap-2">
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className="size-2 animate-bounce rounded-full bg-blue-500 dark:bg-blue-400"
                  style={{ animationDelay: `${index * 0.2}s`, animationDuration: "1.5s" }}
                />
              ))}
            </div>

            {/* Tips */}
            <div className="mt-6 animate-slide-up rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
              <p className="text-center text-xs text-blue-700 dark:text-blue-300">
                <span className="font-medium">{t`Pro Tip:`}</span>{" "}
                {t`Your resume PDF will be downloaded to your device.`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Add CSS animations
const modalKeyframes = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  
  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes progress {
    0% { width: 0%; }
    100% { width: 100%; }
  }
  
  @keyframes scale-in {
    0% { transform: scale(0); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  
  .animate-shimmer {
    animation: shimmer 2s infinite;
  }
  
  .animate-spin-slow {
    animation: spin-slow 2s linear infinite;
  }
  
  .animate-progress {
    animation: progress 3s ease-in-out;
  }
  
  .animate-scale-in {
    animation: scale-in 0.3s ease-out 0.3s both;
  }
`;

// Inject the keyframes
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = modalKeyframes;
  document.head.append(style);
}
